import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Brain, RefreshCw, Send, User, Bot, MessageSquare, Sparkles, Square, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAI } from '@/hooks/useAI';
import { generateResultsExplanationPrompt, detectUserIntent, generateAdaptiveSystemPrompt, type ResultsExplanationContext } from '@/services/ai/ai-prompts';
import { buildNestedPrompts, createNestedPrompt } from '@/services/ai/nested-prompts';
import { getAIWebSocketService } from '@/services/ai/ai-websocket';
import { getAIAutoAnalyze } from '@/services/ai/ai-config';
import { showErrorToast, showSuccessToast } from '@/utils/error-handler';
import MarkdownRenderer from '@/components/ui/markdown';
import type { AIMessage } from '@/services/ai/ai-service';
import { papersApi } from '@/services/api/papers';

const DATA_PREVIEW_ROWS = 50;

interface AIResultsExplanationProps {
  results: Record<string, any>;
  scriptDescription?: string;
  scriptCode?: string;
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
    timezone?: string;
    sessionStrategy?: 'by_date' | 'custom' | 'trading_sessions';
    sessions?: Array<{
      name: string;
      start_time: string;
      end_time: string;
      description?: string;
    }>;
    sessionIncludeDate?: boolean;
    intervalDuration?: number | string;
    intervalIdFormat?: 'timestamp' | 'datetime';
  };
  resultType?: 'table' | 'stats' | 'signals' | 'backtest';
  autoExplain?: boolean;
  scriptId?: string;
  resultId?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  intent?: string; // Store detected intent for user messages
}

export default function AIResultsExplanation({
  results,
  scriptDescription,
  scriptCode,
  datasetInfo,
  resultType,
  autoExplain = false,
  scriptId,
  resultId,
}: AIResultsExplanationProps) {
  const [explanation, setExplanation] = useState<string | null>(null);
  const [isExplanationLoading, setIsExplanationLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatTextareaRef = useRef<HTMLTextAreaElement>(null);
  const chatIdRef = useRef<string>(`chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const wsCleanupRef = useRef<(() => void) | null>(null);

  const [paperDialogOpen, setPaperDialogOpen] = useState(false);
  const [paperTitle, setPaperTitle] = useState('');
  const [isSavingToDb, setIsSavingToDb] = useState(false);
  
  const { callAI, callAIChat, error, isConfigured } = useAI({
    onError: (err) => {
      showErrorToast(err, 'Failed to generate explanation');
      setIsExplanationLoading(false);
    }
  });

  const generateExplanation = async () => {
    if (!isConfigured) {
      showErrorToast('Please configure your AI API key in Settings first');
      return;
    }

    const prompt = generateResultsExplanationPrompt({
      results,
      scriptDescription,
      scriptCode,
      datasetInfo,
      resultType
    });

    const systemPrompt = `You are an expert quantitative analyst (quant) with deep expertise in:
- Systematic trading strategies and signal generation
- Statistical analysis of financial markets
- Risk management and portfolio optimization
- Market microstructure and behavioral patterns
- Technical indicators and their applications

Your analysis should be:
- Data-driven and statistically rigorous
- Actionable for quantitative traders
- Clear about methodology and assumptions
- Focused on practical trading implications
- Professional and suitable for institutional quants

When referring to the analysis script, use correct QuantQuack DSL terminology: config (timezone, sessions), input (literals only), signal name = condition, "stats by date" or "stats by session" with count/percent/avg as alias, "if cond then a else b", and one output (table, stats, or signals).`;

    setIsExplanationLoading(true);
    try {
      const response = await callAI(prompt, systemPrompt, {
        temperature: 0.3, // Lower temperature for more focused, analytical responses
        maxTokens: 1500 // Increased for comprehensive quant analysis
      });

      // Set explanation if we got a response
      if (response) {
        setExplanation(response);
      } else {
        showErrorToast('No response from AI. Please try again.');
      }
    } catch (err) {
      console.error('Error generating explanation:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      showErrorToast(errorMessage, 'Failed to generate explanation');
    } finally {
      setIsExplanationLoading(false);
    }
  };

  // Auto-scroll chat to bottom (including when streaming)
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatLoading, streamingContent, isStreaming]);

  // Auto-resize chat textarea
  useEffect(() => {
    if (chatTextareaRef.current) {
      chatTextareaRef.current.style.height = 'auto';
      chatTextareaRef.current.style.height = `${chatTextareaRef.current.scrollHeight}px`;
    }
  }, [chatInput]);

  useEffect(() => {
    // Use autoExplain prop if provided, otherwise check config setting
    const shouldAutoExplain = autoExplain !== undefined ? autoExplain : getAIAutoAnalyze();
    if (shouldAutoExplain && isConfigured && Object.keys(results).length > 0) {
      generateExplanation();
    }
  }, [autoExplain, isConfigured]);

  const handleChatSend = async () => {
    if (!chatInput.trim()) {
      return;
    }

    if (!isConfigured) {
      showErrorToast('Please configure your AI API key to chat');
      return;
    }

    const userMessage = chatInput.trim();
    setChatInput('');

    // Detect user intent
    const intent = detectUserIntent(userMessage, !!scriptCode);

    // Add user message to chat with intent
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date(),
      intent
    };
    setChatMessages(prev => [...prev, newUserMessage]);
    setIsChatLoading(true);
    setIsStreaming(true);
    setStreamingContent('');
    
    // Generate adaptive system prompt based on intent
    const systemPrompt = generateAdaptiveSystemPrompt(intent, {
      hasScriptCode: !!scriptCode,
      scriptCode,
      results,
      scriptDescription,
      datasetInfo,
      resultType
    });

    // Build nested prompts structure
    const baseContext: ResultsExplanationContext = {
      results,
      scriptDescription,
      scriptCode,
      datasetInfo,
      resultType
    };

    const nestedPrompts = buildNestedPrompts(
      baseContext,
      chatMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      } as AIMessage))
    );

    // Create optimized messages with nested prompts
    const optimizedMessages = createNestedPrompt(
      nestedPrompts,
      systemPrompt,
      userMessage
    );

    // Get AI config
    const { getAIConfig } = await import('@/services/ai/ai-config');
    const config = getAIConfig();
    
    if (!config) {
      showErrorToast('AI configuration not found');
      setIsChatLoading(false);
      setIsStreaming(false);
      return;
    }

    // Use WebSocket streaming if available, otherwise fallback to HTTP
    try {
      const wsService = getAIWebSocketService();
      const chatId = chatIdRef.current;

      // Cleanup previous connection if exists
      if (wsCleanupRef.current) {
        wsCleanupRef.current();
      }

      // Connect to WebSocket and send request
      let fullResponse = '';
      
      wsService.connect(chatId, (message) => {
        if (message.type === 'chunk' && message.content) {
          fullResponse += message.content;
          setStreamingContent(fullResponse);
          // Auto-scroll to bottom when new content arrives
          setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 50);
        } else if (message.type === 'done') {
          setIsStreaming(false);
          setIsChatLoading(false);
          
          // Always add response if we have content, even if empty
          if (fullResponse.trim()) {
            const assistantMessage: ChatMessage = {
              role: 'assistant',
              content: fullResponse.trim(),
              timestamp: new Date()
            };
            setChatMessages(prev => [...prev, assistantMessage]);
          }
          setStreamingContent('');
          fullResponse = '';
        } else if (message.type === 'error') {
          setIsStreaming(false);
          setIsChatLoading(false);
          setStreamingContent('');
          const streamError = new Error(message.error || 'Streaming error');
          showErrorToast(streamError, 'AI streaming error');
        }
      }).then((cleanup) => {
        wsCleanupRef.current = cleanup;

        // Wait for WebSocket to be ready, then send request
        let retryCount = 0;
        const maxRetries = 15; // 15 retries * 200ms = 3 seconds max
        
        const checkAndSend = () => {
          retryCount++;
          
          if (wsService.isConnected(chatId)) {
            const sent = wsService.sendChatRequest(chatId, {
              provider: config.provider,
              apiKey: config.apiKey,
              messages: optimizedMessages,
              model: config.model,
              temperature: intent === 'code_improvement' || intent === 'suggestion_request' ? 0.4 : 0.3,
              maxTokens: 2000
            });

            if (!sent) {
              // Fallback to HTTP if send failed
              handleChatSendHTTP(optimizedMessages, intent);
            }
          } else if (retryCount < maxRetries) {
            // Retry after a short delay
            setTimeout(checkAndSend, 200);
          } else {
            // Max retries reached, fallback to HTTP
            handleChatSendHTTP(optimizedMessages, intent);
          }
        };

        // Start checking after connection
        setTimeout(checkAndSend, 300);
      }).catch(() => {
        // Fallback to HTTP if WebSocket fails
        handleChatSendHTTP(optimizedMessages, intent);
      });
    } catch (wsError) {
      // Fallback to HTTP
      handleChatSendHTTP(optimizedMessages, intent);
    }
  };

  // Stop AI operation
  const handleStop = () => {
    // Stop WebSocket streaming
    if (wsCleanupRef.current) {
      const wsService = getAIWebSocketService();
      wsService.stopStreaming(chatIdRef.current);
      wsCleanupRef.current();
      wsCleanupRef.current = null;
    }

    // Reset states (this will prevent HTTP responses from being processed)
    setIsStreaming(false);
    setIsChatLoading(false);
    setIsExplanationLoading(false);
    setStreamingContent('');
  };

  // Fallback HTTP handler
  const handleChatSendHTTP = async (
    conversationHistory: AIMessage[],
    intent: string
  ) => {
    setIsStreaming(false);
    
    const temperature = intent === 'code_improvement' || intent === 'suggestion_request' ? 0.4 : 0.3;

    try {
      const response = await callAIChat(conversationHistory, {
        temperature,
        maxTokens: 2000
      });

      // Always add response if we got one
      if (response) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      // Only show error if not stopped
      if (isChatLoading) {
        throw err;
      }
    } finally {
      setIsChatLoading(false);
    }
  };


  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSend();
    }
  };

  // --- Save as paper ---
  function getDataPreviewForPaper(): { markdown: string; dataPreview: unknown } {
    const raw = results?.data ?? results;
    if (Array.isArray(raw)) {
      const slice = raw.slice(0, DATA_PREVIEW_ROWS);
      const cols = slice.length > 0 ? Object.keys(slice[0] as object) : [];
      const header = cols.join(' | ');
      const sep = cols.map(() => '---').join(' | ');
      const rows = slice.map((row: Record<string, unknown>) =>
        cols.map((c) => String((row as Record<string, unknown>)[c] ?? '')).join(' | ')
      );
      const total = raw.length;
      const markdown = `\n**Data preview** (first ${Math.min(DATA_PREVIEW_ROWS, total)} of ${total} rows)\n\n| ${header} |\n| ${sep} |\n${rows.map((r) => '| ' + r + ' |').join('\n')}\n`;
      return { markdown, dataPreview: { rows: slice, total, columns: cols } };
    }
    const obj = typeof raw === 'object' && raw !== null ? raw : { value: raw };
    const markdown = '\n**Data preview**\n\n```json\n' + JSON.stringify(obj, null, 2) + '\n```\n';
    return { markdown, dataPreview: obj };
  }

  function buildPaperMarkdown(title: string): string {
    const { markdown: dataPreviewMd } = getDataPreviewForPaper();
    const sections: string[] = [];
    sections.push('# ' + (title || 'AI Analysis Paper'));
    sections.push('\n---\n');
    sections.push('## Context');
    sections.push(`- **Result type:** ${resultType ?? 'table'}`);
    if (scriptDescription) sections.push(`- **Script:** ${scriptDescription}`);
    if (datasetInfo?.symbol) sections.push(`- **Symbol:** ${datasetInfo.symbol}`);
    if (datasetInfo?.timeframe) sections.push(`- **Timeframe:** ${datasetInfo.timeframe}`);
    if (datasetInfo?.timezone) sections.push(`- **Timezone:** ${datasetInfo.timezone}`);
    sections.push(`- **Saved:** ${new Date().toISOString()}`);
    sections.push('\n---\n');
    if (explanation) {
      sections.push('## AI Explanation\n\n');
      sections.push(explanation);
      sections.push('\n\n---\n');
    }
    if (chatMessages.length > 0) {
      sections.push('## Chat\n\n');
      chatMessages.forEach((m) => {
        sections.push(`**${m.role === 'user' ? 'User' : 'Assistant'}:**\n\n${m.content}\n\n`);
      });
      sections.push('---\n');
    }
    sections.push('## Data Preview');
    sections.push(dataPreviewMd);
    if (scriptCode) {
      sections.push('\n---\n## Script (DSL)\n\n```\n');
      sections.push(scriptCode);
      sections.push('\n```\n');
    }
    return sections.join('\n');
  }

  function buildPaperJson(title: string): Record<string, unknown> {
    const { dataPreview } = getDataPreviewForPaper();
    return {
      title: title || 'AI Analysis Paper',
      savedAt: new Date().toISOString(),
      context: {
        resultType: resultType ?? 'table',
        scriptDescription: scriptDescription ?? undefined,
        scriptCode: scriptCode ?? undefined,
        datasetInfo: datasetInfo ?? undefined,
      },
      explanation: explanation ?? undefined,
      chat: chatMessages.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp.toISOString(),
        intent: m.intent,
      })),
      dataPreview,
    };
  }

  function downloadPaperAsMarkdown() {
    const title = paperTitle.trim() || `analysis-${new Date().toISOString().slice(0, 10)}`;
    const md = buildPaperMarkdown(title);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-zA-Z0-9-_]/g, '-')}.md`;
    a.click();
    URL.revokeObjectURL(url);
    setPaperDialogOpen(false);
    setPaperTitle('');
    showSuccessToast('Paper saved as Markdown');
  }

  function downloadPaperAsJson() {
    const title = paperTitle.trim() || `analysis-${new Date().toISOString().slice(0, 10)}`;
    const json = buildPaperJson(title);
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-zA-Z0-9-_]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setPaperDialogOpen(false);
    setPaperTitle('');
    showSuccessToast('Paper saved as JSON');
  }

  async function savePaperToDatabase() {
    const title = paperTitle.trim() || `analysis-${new Date().toISOString().slice(0, 10)}`;
    const payload = buildPaperJson(title);
    setIsSavingToDb(true);
    try {
      await papersApi.create({
        title,
        payload,
        ...(scriptId && { scriptId }),
        ...(resultId && { resultId }),
      });
      setPaperDialogOpen(false);
      setPaperTitle('');
      showSuccessToast('Paper saved to database');
    } catch (err) {
      showErrorToast(err, 'Failed to save paper');
    } finally {
      setIsSavingToDb(false);
    }
  }

  // Format intent name for display
  const formatIntentName = (intent: string): string => {
    const intentMap: Record<string, string> = {
      'code_improvement': 'Code Improvement',
      'code_explanation': 'Code Explanation',
      'analysis_question': 'Analysis Question',
      'methodology_question': 'Methodology',
      'suggestion_request': 'Suggestion',
      'error_fix': 'Error Fix',
      'general_question': 'General Question',
      'code_generation': 'Code Generation',
      'learn_dsl': 'Learn DSL',
      'learn_algorithmic_trading': 'Learn Trading',
      'unknown': 'Unknown'
    };
    return intentMap[intent] || intent;
  };

  if (!isConfigured) {
    return null; // Don't show anything if AI is not configured
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="w-5 h-5" />
          Results Explanation
        </CardTitle>
        <CardDescription>
          Get plain-language explanations of your statistical results
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!explanation && !isExplanationLoading && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={generateExplanation}
              variant="outline"
              className="flex-1 min-w-0"
            >
              <Brain className="w-4 h-4 mr-2" />
              Explain Results
            </Button>
            <Button
              onClick={() => setPaperDialogOpen(true)}
              variant="outline"
              className="flex-1 min-w-0"
            >
              <FileText className="w-4 h-4 mr-2" />
              Save as paper
            </Button>
          </div>
        )}

        {isExplanationLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Analyzing results...</span>
            <Button
              onClick={handleStop}
              variant="ghost"
              size="sm"
              className="ml-4"
            >
              <Square className="w-4 h-4" />
            </Button>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {explanation && (
          <div className="space-y-3">
            <div className="p-4 bg-muted/50 rounded-lg">
              <MarkdownRenderer content={explanation} className="text-sm" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setShowChat(!showChat)}
                variant="outline"
                size="sm"
                className="flex-1 min-w-0"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {showChat ? 'Hide Chat' : 'Chat About Results'}
              </Button>
              <Button
                onClick={generateExplanation}
                variant="outline"
                size="sm"
                className="flex-1 min-w-0"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
              <Button
                onClick={() => setPaperDialogOpen(true)}
                variant="outline"
                size="sm"
                className="flex-1 min-w-0"
              >
                <FileText className="w-4 h-4 mr-2" />
                Save as paper
              </Button>
            </div>

            {/* Chat Interface */}
            {showChat && (
              <div className="border rounded-lg flex flex-col" style={{ height: '400px' }}>
                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {chatMessages.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-8">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="font-medium mb-2">AI Assistant - Multi-Purpose Chat</p>
                      <p className="text-xs mb-3">I can help you with:</p>
                      <div className="text-xs space-y-1 text-left max-w-md mx-auto">
                        <p>• <strong>Analysis questions:</strong> "What does this mean for risk management?"</p>
                        <p>• <strong>Code improvements:</strong> "How can I optimize this script?"</p>
                        <p>• <strong>Code explanations:</strong> "Explain this code"</p>
                        <p>• <strong>Suggestions:</strong> "What improvements would you suggest?"</p>
                        <p>• <strong>Learn DSL:</strong> "Teach me QuantQuack DSL"</p>
                        <p>• <strong>Learn trading:</strong> "Explain algorithmic trading"</p>
                        <p>• <strong>Methodology:</strong> "What's the best approach for this?"</p>
                      </div>
                      {scriptCode && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs flex items-center justify-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            I'll proactively suggest code improvements when relevant
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {chatMessages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs ${
                        message.role === 'user' 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted border'
                      }`}>
                        {message.role === 'user' ? (
                          <User className="w-3.5 h-3.5" />
                        ) : (
                          <Bot className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <div className="flex-1 max-w-[85%] min-w-0">
                        <div
                          className={`rounded p-2 text-xs ${
                            message.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted border'
                          }`}
                        >
                          <MarkdownRenderer content={message.content} className="text-xs" />
                        </div>
                        {message.role === 'user' && message.intent && message.intent !== 'unknown' && message.intent !== 'general_question' && (
                          <div className="mt-1 flex justify-end">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-auto">
                              {formatIntentName(message.intent)}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Show streaming content if available */}
                  {isStreaming && streamingContent && (
                    <div className="flex gap-2 flex-row">
                      <div className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center bg-muted border">
                        <Bot className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 max-w-[85%] min-w-0 rounded p-2 text-xs bg-muted border">
                        <div className="relative">
                          <MarkdownRenderer content={streamingContent} className="text-xs" />
                          {/* Streaming cursor indicator */}
                          <span className="inline-block w-0.5 h-3 ml-0.5 bg-primary animate-pulse" style={{ verticalAlign: 'text-bottom' }} />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Show streaming indicator even if no content yet */}
                  {isStreaming && !streamingContent && (
                    <div className="flex gap-2 flex-row">
                      <div className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center bg-muted border">
                        <Bot className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 max-w-[85%] min-w-0 rounded p-2 text-xs bg-muted border flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Thinking...</span>
                      </div>
                    </div>
                  )}
                  {isChatLoading && !isStreaming && (
                    <div className="flex justify-start gap-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center bg-muted border">
                        <Bot className="w-3.5 h-3.5" />
                      </div>
                      <div className="bg-muted border rounded p-2 flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Analyzing...</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Chat Input */}
                <div className="p-2 border-t bg-muted/20 flex-shrink-0">
                  <div className="flex gap-2 items-end">
                    <Textarea
                      ref={chatTextareaRef}
                      placeholder="Ask about the analysis..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyDown={handleChatKeyDown}
                      className="min-h-[60px] max-h-[120px] resize-none text-xs"
                      rows={2}
                    />
                    {(isChatLoading || isStreaming) ? (
                      <Button
                        onClick={handleStop}
                        variant="destructive"
                        size="sm"
                        className="h-[60px]"
                      >
                        <Square className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleChatSend}
                        disabled={!chatInput.trim() || isChatLoading}
                        size="sm"
                        className="h-[60px]"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Save as paper dialog */}
      <Dialog open={paperDialogOpen} onOpenChange={setPaperDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Save as paper
            </DialogTitle>
            <DialogDescription>
              Export this AI analysis with explanation, chat, data preview, and context. Choose a title and format.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="paper-title">Paper title (optional)</Label>
            <Input
              id="paper-title"
              placeholder="e.g. NQ 5m signal analysis"
              value={paperTitle}
              onChange={(e) => setPaperTitle(e.target.value)}
              className="w-full"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-wrap">
            <Button variant="outline" onClick={() => setPaperDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={savePaperToDatabase}
              disabled={isSavingToDb}
            >
              {isSavingToDb ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save to database'
              )}
            </Button>
            <Button variant="outline" onClick={downloadPaperAsJson}>
              Save as JSON
            </Button>
            <Button variant="outline" onClick={downloadPaperAsMarkdown}>
              Save as Markdown
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
